import { List, ListItem, Stack } from '@mui/material';
import { useAtom } from 'jotai';
import { useMemo } from 'react';

import REPORT_PARAGRAPHS from '@/constants/reportparagraph.constants';
import FunnelWidget from '@/features/FunnelWidget';
import ReportsTemplateBoxContainer from '@/features/ReportsTemplateBoxContainer/ReportsTemplateBoxContainer';
import ReportsTemplateCompareWidget from '@/features/ReportsTemplateCompareWidget/ReportsTemplateCompareWidget';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import { ReportsTemplateSectionTitle } from '@/features/ReportsTemplateSectionTitle/ReportsTemplateSectionTitle';
import type { Ticket } from '@/pages/Tickets';
import { formatLargeNumber } from '@/utils/Common.utils';

import {
  reportsLogAvgAtom,
  reportsNetworkAvgAtom
} from '../ReportsTemplate.hooks';
import type { FunnelProps } from './ReportsPage.types';
import useStyles from './ReportsPages.styles';

const ReportsPageExecFunnel = ({
  activeProductTypes,
  diff,
  usageDataRaw,
  usageLimitsDataRaw,
  eventsDataRaw,
  casesDataRaw,
  ticketedCases,
  ticketsDataRaw
}: FunnelProps) => {
  const { classes } = useStyles();
  const [networkAvg] = useAtom(reportsNetworkAvgAtom);
  const [logAvg] = useAtom(reportsLogAvgAtom);

  const totalLogEvents = eventsDataRaw
    .filter((e) => e.type === 'log')
    .reduce((acc, curr) => acc + (curr.count || 0), 0);
  const totalNetEvents = eventsDataRaw
    .filter((e) => e.type === 'net')
    .reduce((acc, curr) => acc + (curr.count || 0), 0);
  const totalEvents = eventsDataRaw.reduce(
    (acc, curr) => acc + (curr.count || 0),
    0
  );

  const totalCases = casesDataRaw.reduce((acc: number, curr) => acc + curr, 0);

  const totalNewTickets = ticketsDataRaw.filter(
    (e: Ticket) => e.ticket_type === 1
  ).length;

  // Usage Decimals
  const decimalPlaces = 2;
  const decimalFactor = 10 ** decimalPlaces;

  // Log Usage Data Parsing
  const logIngestEntries = usageDataRaw?.filter((e) => e.type === 'log');
  const previousLogWindow = logIngestEntries?.slice(diff, diff * 2);

  const sumGB = (entries: typeof logIngestEntries) =>
    entries.reduce((acc, curr) => acc + (curr.gb || 0), 0);

  const avgGB = (entries: typeof logIngestEntries) =>
    entries.length > 0 ? sumGB(entries) / entries.length : 0;

  const previousLogAvg = previousLogWindow
    ? Math.ceil(avgGB(previousLogWindow) * decimalFactor) / decimalFactor
    : 0;
  const logIngestChange =
    previousLogAvg === 0
      ? 0
      : ((logAvg - previousLogAvg) / previousLogAvg) * 100;

  // Network Usage Data Parsing
  const networkIngestEntries = usageDataRaw?.filter((e) => e.type === 'net');
  const previousNetWindow = networkIngestEntries?.slice(diff, diff * 2);

  const sumMBPS = (entries: typeof networkIngestEntries) =>
    entries.reduce((acc, curr) => acc + (curr.mb_per_second || 0), 0);

  const avgMBPS = (entries: typeof networkIngestEntries) =>
    entries?.length > 0 ? sumMBPS(entries) / entries.length : 0;

  const previousNetAvg = previousNetWindow
    ? Math.ceil(avgMBPS(previousNetWindow) * decimalFactor) / decimalFactor
    : 0;

  const networkIngestChange =
    previousNetAvg === 0
      ? 0
      : ((networkAvg - previousNetAvg) / previousNetAvg) * 100;

  // Parse usage limits data
  const logUsageLimit =
    usageLimitsDataRaw?.find((e) => e.code === 'POND-LOG') // Checking against code because multiple products may have LOG product type
      ?.subscription_limit || 0;
  const networkUsageLimit =
    usageLimitsDataRaw?.find((e) => e.product_type === 'NET')
      ?.subscription_limit || 0;

  const mdrFunnelData = useMemo(() => {
    const eventValues = [];

    if (activeProductTypes.includes('LOG')) {
      eventValues.push({
        title: 'Logs',
        value: `${formatLargeNumber(totalLogEvents)}`
      });
    }

    if (activeProductTypes.includes('NET')) {
      eventValues.push({
        title: 'Network',
        value: `${formatLargeNumber(totalNetEvents)}`
      });
    }

    if (
      activeProductTypes.includes('EDR') &&
      !activeProductTypes.includes('LOG')
    ) {
      eventValues.push({
        title: 'EDR',
        value: `${formatLargeNumber(totalLogEvents)}`
      });
    }

    return [
      {
        label: 'EVENTS',
        values: eventValues,
        color: '#026670'
      },
      {
        label: 'CASES',
        values: [
          {
            title: '',
            value: `${formatLargeNumber(totalCases)}`
          }
        ],
        color: '#7B1FA2'
      },
      {
        label: 'NEW TICKETS',
        values: [{ title: '', value: `${totalNewTickets}` }],
        color: '#C62828'
      }
    ];
  }, [
    activeProductTypes,
    totalLogEvents,
    totalNetEvents,
    totalCases,
    totalNewTickets
  ]);

  const totalTicketedCasesInTimeframe = useMemo(() => {
    // Sum the count of ticketed cases in the selected timeframe
    return ticketedCases.reduce((acc, curr) => acc + (curr.count || 0), 0);
  }, [ticketedCases]);

  const mdrFunnelDescription = useMemo(() => {
    let totalEventsFormatted = formatLargeNumber(totalEvents, true);
    let totalCasesFormatted = formatLargeNumber(totalCases, true);
    let totalTicketedCases = formatLargeNumber(
      totalTicketedCasesInTimeframe,
      true
    );
    let totalNewTicketsFormatted = totalNewTickets.toString();

    const noCaseTickets = totalNewTickets - totalTicketedCasesInTimeframe;
    if (totalEvents === 0) {
      totalEventsFormatted = 'no';
    }
    if (totalCases === 0) {
      totalCasesFormatted = 'no';
    }
    if (totalNewTickets === 0) {
      totalNewTicketsFormatted = 'no investigated cases';
    }
    if (totalTicketedCasesInTimeframe === 0) {
      totalTicketedCases = 'none';
    }

    if (totalNewTickets === 0 && totalCases > 0) {
      return (
        <>
          During this reporting period, we monitored{' '}
          <strong style={{ color: '#026670' }}>{totalEventsFormatted}</strong>{' '}
          events across your IT and security infrastructure. Potential threats
          are investigated through cases using Kanati, SOC analysts, or both.
          Cases are visible in Scope and may be concluded without escalation or
          escalated as tickets when customer engagement is needed. During this
          period,{' '}
          <strong style={{ color: '#7B1FA2' }}>
            {totalCasesFormatted} cases
          </strong>{' '}
          cases were created and{' '}
          <strong style={{ color: '#C62828' }}>
            {noCaseTickets} new tickets
          </strong>{' '}
          originated from cases.
        </>
      );
    }
    if (totalCases === 0 && totalNewTickets > 0) {
      return (
        <>
          During this reporting period, we monitored{' '}
          <strong style={{ color: '#026670' }}>{totalEventsFormatted}</strong>{' '}
          events across your IT and security infrastructure. Using advanced
          detection techniques such as{' '}
          <strong>
            automated filtering, threshold tuning, machine learning, and
            correlation logic
          </strong>
          , no cases were identified through our standard log and network
          pipeline.
          <br />
          <br />
          Our 24x7 SOC continuously analyzed incoming telemetry and validated
          detection logic to ensure integrity and effectiveness.
          <br />
          <br />
          Despite the absence of cases,{' '}
          <strong>{totalNewTicketsFormatted}</strong> new{' '}
          {totalNewTicketsFormatted === '1' ? 'ticket was' : 'tickets were'}{' '}
          created through other proactive measures, including threat hunts,
          threat intelligence signals, and infrastructure monitoring that fall
          outside of the standard case pipeline—demonstrating layered visibility
          beyond event-driven detections.
        </>
      );
    }
    if (totalCases === 0 && totalNewTickets === 0) {
      return (
        <>
          During this reporting period, we monitored{' '}
          <strong style={{ color: '#026670' }}>{totalEventsFormatted}</strong>{' '}
          events across your IT and security infrastructure. Our detection
          systems applied{' '}
          <strong>
            automated filtering, threshold tuning, machine learning, and
            correlation logic
          </strong>{' '}
          to identify potential threats. No cases were generated through our
          standard log and network pipeline.
          <br />
          <br />
          Our 24x7 SOC actively validated the health and fidelity of the
          telemetry and detection logic to confirm normal activity levels and
          ensure continuous visibility.
          <br />
          <br />
          No tickets were created during this period, as there were no
          findings—automated or human-initiated—that required customer-facing
          escalation. While this quiet period may reflect a stable threat
          landscape, we remain vigilant in continuously monitoring for changes
          or emerging risks.
        </>
      );
    }
    return (
      <>
        During this reporting period, we monitored{' '}
        <strong style={{ color: '#026670' }}>{totalEventsFormatted}</strong>{' '}
        events across your IT and security infrastructure. Using advanced
        detection techniques such as{' '}
        <strong>
          automated filtering, threshold tuning, machine learning, and
          correlation logic
        </strong>
        , we identified{' '}
        <strong style={{ color: '#7B1FA2' }}>
          {totalCasesFormatted} cases
        </strong>{' '}
        through our standard log and network pipeline for further review.
        <br />
        <br />
        Each was assessed by our 24x7 SOC using a mix of{' '}
        <strong>human expertise and AI-assisted workflows.</strong>
        <br />
        <br />
        Of the,{' '}
        <strong style={{ color: '#C62828' }}>
          {totalNewTicketsFormatted} new tickets
        </strong>{' '}
        created,{' '}
        <strong style={{ color: '#C62828' }}>{totalTicketedCases}</strong>{' '}
        stemmed from these investigated cases.
        {noCaseTickets > 0 && (
          <>
            The other{' '}
            <strong style={{ color: '#C62828' }}>{noCaseTickets}</strong>{' '}
            {noCaseTickets === 1 ? 'was ' : 'were '}
            initiated through separate detection efforts, including{' '}
            <strong>
              threat hunts, threat intelligence signals, and infrastructure
              monitoring
            </strong>{' '}
            that falls outside of the standard case pipeline, ensuring broader
            visibility beyond event-driven cases.
          </>
        )}
      </>
    );
  }, [
    totalLogEvents,
    totalNetEvents,
    totalCases,
    totalNewTickets,
    totalTicketedCasesInTimeframe
  ]);

  return (
    <>
      <ReportParagraph fontSize="10px">
        {REPORT_PARAGRAPHS.executiveSummary}
      </ReportParagraph>
      <ReportsTemplateSectionTitle>
        PONDURANCE SECURITY OPERATIONS PIPELINE
      </ReportsTemplateSectionTitle>
      <FunnelWidget
        data={mdrFunnelData}
        description={mdrFunnelDescription}
        widthFactor={20}
      />
      <div>
        <ReportParagraph fontSize="10px">
          {REPORT_PARAGRAPHS.funnelDescription}
        </ReportParagraph>
        <List
          dense
          sx={{
            listStyleType: 'disc',
            fontSize: '10px',
            padding: '8px 24px 0 24px',
            '& .MuiListItem-root': {
              marginBottom: '8px' // reduce spacing between items
            }
          }}
        >
          <ListItem className={classes.execFunnelListItems}>
            <strong>Events</strong> include raw telemetry (e.g., log and network
            data) and cases ingested from managed and monitored platforms such
            as EDR, firewall, and email security tools.
          </ListItem>
          <ListItem className={classes.execFunnelListItems}>
            <strong>Cases</strong> are investigation records visible in Scope
            that capture the analysis and outcome of potential security threats.
            Cases may be investigated by Kanati, SOC analysts, or both, and may
            conclude with or without escalation to a ticket.
          </ListItem>
          <ListItem className={classes.execFunnelListItems}>
            <strong>New Tickets</strong> are opened when a case warrants direct
            engagement with your team.
          </ListItem>
        </List>
      </div>
      <Stack direction="row" spacing={2}>
        {activeProductTypes.includes('LOG') && (
          <ReportsTemplateBoxContainer
            subChildren={REPORT_PARAGRAPHS.funnelLogIngest}
          >
            <ReportsTemplateCompareWidget
              title="Avg. Log Ingest Volume (GB/day)"
              value={`${logAvg}`}
              maxValue={`${logUsageLimit}`}
              percentChange={logIngestChange}
            />
          </ReportsTemplateBoxContainer>
        )}

        {activeProductTypes.includes('NET') && (
          <ReportsTemplateBoxContainer
            subChildren={REPORT_PARAGRAPHS.funnelNetworkIngest}
          >
            <ReportsTemplateCompareWidget
              title="Avg. Network Bandwidth (Mbps)"
              value={`${networkAvg}`}
              maxValue={`${networkUsageLimit}`}
              percentChange={networkIngestChange}
            />
          </ReportsTemplateBoxContainer>
        )}
      </Stack>
    </>
  );
};

export default ReportsPageExecFunnel;
